import { Link } from 'react-router-dom';
import {
  ABILITY_INFO, ALL_PIECES, createGame, defaultRules, pieceActions, sqName,
  type Action, type GameState, type PieceTypeDef,
} from '@ic/engine';
import { Board } from '../components/Board';
import { Icon } from '../components/Icons';
import { pieceMoveText } from '../lib/pieceText';

/** A 7×7 board with one piece in the middle and its moves highlighted. */
export function MoveDiagram({ def, size = 7 }: { def: PieceTypeDef; size?: number }) {
  const mid = sqName(Math.floor(size / 2) * size + Math.floor(size / 2), size);
  const s: GameState = createGame({
    schema: 1, id: 'diagram', name: def.name, board: { width: size, height: size, voids: [], walls: [] },
    pieces: { [def.id]: { ...def, abilities: [] } }, setup: [{ sq: mid, type: def.id, color: 'w' }],
    rules: { ...defaultRules(), castling: false, promotion: { enabled: false, zone: 1, choices: [] } },
  });
  const acts: Action[] = [];
  const from = s.board.findIndex(Boolean);
  pieceActions(s, from, acts);
  const marks: Record<number, string> = {};
  for (const a of acts) if (a.t === 'move') marks[a.to] = 'mark-move';
  // Capture-only squares (like a pawn's diagonals) are shown in a second color.
  for (const rule of def.moves) {
    if (rule.mode !== 'capture') continue;
    for (const [dx, dy] of rule.vectors) {
      const x = (from % size) + dx;
      const y = Math.floor(from / size) + dy;
      if (x >= 0 && y >= 0 && x < size && y < size) marks[y * size + x] = 'mark-capture';
    }
  }
  return <div className="diagram"><Board state={s} orientation="w" marks={marks} /></div>;
}

const CUSTOM = ['dragon', 'bomb', 'miner', 'frog', 'gardener', 'elephant', 'cockroach', 'cook'];
const STANDARD = ['king', 'queen', 'rook', 'bishop', 'knight', 'pawn'];

export function Learn() {
  return (
    <div className="page learn">
      <h1>Learn</h1>
      <section className="card">
        <h2>The basics</h2>
        <p>
          Each side starts with a king. You win by <b>checkmate</b>: attacking the enemy king so it cannot escape.
          A player whose king is attacked is in <b>check</b> and must get out of it. With no legal move and no check it is
          <b> stalemate</b> (a draw). Games are also drawn by threefold repetition, the 50-move rule, insufficient
          material or agreement.
        </p>
        <p>
          Special moves: <b>castling</b> (king moves two squares toward an unmoved rook, the rook jumps over),
          <b> en passant</b> (a pawn that just moved two squares can be captured as if it moved one) and
          <b> promotion</b> (a pawn reaching the last rank becomes another piece).
        </p>
      </section>

      <h2>Standard pieces</h2>
      <div className="learn-grid">
        {STANDARD.map((id) => (
          <div key={id} className="card learn-piece">
            <MoveDiagram def={ALL_PIECES[id]} />
            <h3>{ALL_PIECES[id].name}</h3>
          </div>
        ))}
      </div>

      <h2>New pieces</h2>
      <p className="muted">Every number below can be changed in the <Link to="/editor">Rule Editor</Link>.</p>
      <div className="learn-custom">
        {CUSTOM.map((id) => {
          const def = ALL_PIECES[id];
          return (
            <div key={id} className="card learn-piece wide">
              <MoveDiagram def={def} />
              <div>
                <h3>{def.name}</h3>
                <p>{pieceMoveText(def)}</p>
                <ul className="ability-list">
                  {def.abilities.map((a) => (
                    <li key={a.kind}>
                      <b><Icon name={a.kind} size={17} /> {ABILITY_INFO[a.kind].name}</b>
                      {a.cooldown > 0 && <span className="tag">cooldown {a.cooldown}</span>}
                      <div className="muted small">{ABILITY_INFO[a.kind].desc}</div>
                    </li>
                  ))}
                </ul>
                {id === 'dragon' && <p className="small muted">After Fire Breath the dragon is exhausted and cannot act on its next turn. Optional rule <i>Scorched Earth</i>: the breath leaves burning squares for 2 rounds.</p>}
                {id === 'bomb' && <p className="small muted">A bomb cannot detonate if the blast would destroy its own king, unless the ruleset allows it. An armed bomb shows its countdown to both players.</p>}
                {id === 'miner' && <p className="small muted">Max 2 active mines per miner. The opponent only learns about a mine when it goes off.</p>}
                {id === 'frog' && <p className="small muted">The tongue reaches up to 3 squares and needs a free line; the enemy lands on the square right next to the frog, which makes it easy prey. Pull it across a hole and it falls in.</p>}
                {id === 'elephant' && <p className="small muted">A stampede reaches up to 3 squares and counts as an attack on every square it runs over, so a king hiding behind a pawn can be in check. Trumpet also stuns a king: if it is in check and cannot move, that is mate.</p>}
                {id === 'cockroach' && <p className="small muted">The extra life works against every kind of capture: moves, fire, explosions, mines and stampedes. Multiply works again after its cooldown, as long as fewer than 2 of its young are alive. The young are just as tough but cannot multiply themselves.</p>}
                {id === 'cook' && <p className="small muted">Feast is perfect after a Fire Breath or a Trumpet: the exhausted or stunned ally can act again straight away. Pan Toss reaches 2 squares and needs a free line.</p>}
                {id === 'gardener' && <p className="small muted">Up to 2 hedges per gardener, each lasting 3 rounds. Hedges stop sliding pieces, dragon fire and pushes. Tending counters Scorched Earth and the Miner's dug holes.</p>}
              </div>
            </div>
          );
        })}
      </div>

      <section className="card">
        <h2>Using abilities</h2>
        <p>Select a piece that has abilities and the ability bar appears under the board. Hover an ability to see its range; targeted abilities then ask you to click a highlighted square. Cooldowns are shown on the buttons.</p>
      </section>
    </div>
  );
}
